import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  buildChildrenOf, buildHidden, toggle,
  FiltrosBarra, CardsResumen, TableHeader, FilaCargando, FilaVacia, FilaArbol, BotonPDF,
  BotonExcel, useExcelDownload, CheckPill,
} from "./BalanceComprobacion";
import { usePDFIndividual } from "./EstadoFinanciero";
import { useCurrency } from "../../../../shared/contexts/CurrencyContext";

const TIPO_COLOR_NIVEL1 = { INGRESO: "#4ade80", GASTO: "#f87171" };

function prevFiscalYear(fechaDesde) {
  if (!fechaDesde) return { desde: null, hasta: null };
  const year = new Date(fechaDesde + "T00:00:00").getFullYear() - 1;
  return { desde: `${year}-01-01`, hasta: `${year}-12-31` };
}

function colorFila(fila) {
  if (fila.nivel === 1) return TIPO_COLOR_NIVEL1[fila.tipo] ?? "#f97316";
  if (fila.nivel === 2) return TIPO_COLOR_NIVEL1[fila.tipo] ?? "var(--text-primary)";
  return fila.nivel === 3 ? "var(--text-secondary)" : "var(--text-muted)";
}

// Separador entre secciones
function Separador({ label, color, numCols }) {
  return (
    <tr>
      <td colSpan={numCols} style={{ padding: "7px 14px",
        background: color + "22",
        borderLeft: `3px solid ${color}`,
        color, fontSize: "0.78rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.8px" }}>
        {label}
      </td>
    </tr>
  );
}

// Fila de total de sección
function FilaTotal({ label, monto, montoAnt, color, comparativo, fmtCRC }) {
  return (
    <tr style={{ borderTop: `1px solid ${color}40` }}>
      <td colSpan={2} style={{ padding: "8px 12px", paddingLeft: 26,
        fontWeight: 700, color, fontSize: "0.85rem" }}>{label}</td>
      <td className="text-end" style={{ padding: "8px 14px",
        fontFamily: "monospace", fontWeight: 800, color, fontSize: "0.88rem" }}>
        {fmtCRC(monto)}
      </td>
      {comparativo && (
        <td className="text-end" style={{ padding: "8px 14px",
          fontFamily: "monospace", fontWeight: 800, color: "var(--text-secondary)", fontSize: "0.88rem",
          borderLeft: "1px solid var(--border-card)" }}>
          {montoAnt != null ? fmtCRC(montoAnt) : "—"}
        </td>
      )}
    </tr>
  );
}

export default function EstadoResultados() {
  const { t } = useTranslation();
  const { fmtCRC, isUSD, setMultimoneda } = useCurrency();

  // valorFila: uses USD field when toggle is active
  function valorFila(fila) {
    const s = Number(isUSD ? (fila.saldo_usd ?? 0) : (fila.saldo ?? 0));
    if (Math.abs(s) < 0.01) return { text: "—", color: "var(--text-muted)" };
    const color = fila.tipo === "INGRESO" ? "#4ade80" : "#f87171";
    return { text: fmtCRC(s), color: fila.nivel <= 2 ? color : "var(--text-secondary)" };
  }

  function valorFilaAnt(fila, dataAnt) {
    if (!dataAnt) return { text: "—", color: "var(--text-muted)" };
    const ant = (dataAnt.filas ?? []).find(f => f.id === fila.id);
    if (!ant) return { text: "—", color: "var(--text-muted)" };
    const s = Number(isUSD ? (ant.saldo_usd ?? 0) : (ant.saldo ?? 0));
    if (Math.abs(s) < 0.01) return { text: "—", color: "var(--text-muted)" };
    return { text: fmtCRC(s), color: "var(--text-secondary)" };
  }

  const now       = new Date();
  const mesInicio = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const mesFin    = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split("T")[0];

  const [fechaDesde,    setFechaDesde]    = useState(mesInicio);
  const [fechaHasta,    setFechaHasta]    = useState(mesFin);
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [soloConMov,    setSoloConMov]    = useState(true);
  const [collapsed,     setCollapsed]     = useState(new Set());
  const [comparativo,   setComparativo]   = useState(false);
  const [dataAnt,       setDataAnt]       = useState(null);
  const [loadingAnt,    setLoadingAnt]    = useState(false);
  const [pdfMensual,    setPdfMensual]    = useState(false);

  const pdfER = usePDFIndividual(
    "/api/finance/reportes/estado-resultados-pdf/",
    "estado_resultados.pdf",
    { showComparativo: true, showNotas: true }
  );
  const excelER = useExcelDownload(
    "/api/finance/reportes/estado-resultados-excel/",
    "estado_resultados.xlsx"
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaDesde) qs.set("fecha_desde", fechaDesde);
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/estado-resultados/?${qs}`);
      const d   = await res.json();
      setData(d);
      setMultimoneda(d.multimoneda_activa ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, setMultimoneda]);

  const fetchDataAnt = useCallback(async () => {
    if (!comparativo) { setDataAnt(null); return; }
    const { desde, hasta } = prevFiscalYear(fechaDesde);
    if (!desde) return;
    setLoadingAnt(true);
    try {
      const qs = new URLSearchParams();
      qs.set("fecha_desde", desde);
      qs.set("fecha_hasta", hasta);
      const res = await apiFetch(`/api/finance/reportes/estado-resultados/?${qs}`);
      const d   = await res.json();
      setDataAnt(d);
    } catch (e) { console.error(e); }
    finally { setLoadingAnt(false); }
  }, [comparativo, fechaDesde]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDataAnt(); }, [fetchDataAnt]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const ingresos = filas.filter(f => f.tipo === "INGRESO" && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id)));
  const gastos   = filas.filter(f => f.tipo === "GASTO"   && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id)));

  // Use USD totals when toggle is active — amounts come directly from backend
  const tI  = Number(isUSD ? (data?.total_ingresos_usd ?? 0) : (data?.total_ingresos ?? 0));
  const tG  = Number(isUSD ? (data?.total_gastos_usd   ?? 0) : (data?.total_gastos   ?? 0));
  const res = Number(isUSD ? (data?.resultado_usd      ?? 0) : (data?.resultado      ?? 0));
  const sup = data?.es_superavit ?? true;

  const tIAnt  = comparativo && dataAnt ? Number(isUSD ? (dataAnt.total_ingresos_usd ?? 0) : (dataAnt.total_ingresos ?? 0)) : null;
  const tGAnt  = comparativo && dataAnt ? Number(isUSD ? (dataAnt.total_gastos_usd   ?? 0) : (dataAnt.total_gastos   ?? 0)) : null;
  const resAnt = comparativo && dataAnt ? Number(isUSD ? (dataAnt.resultado_usd      ?? 0) : (dataAnt.resultado      ?? 0)) : null;

  const numCols = comparativo ? 4 : 3;
  const pfy = prevFiscalYear(fechaDesde);

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.estadoResultados")}</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.estadoResultados")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("reportes.ingresosGastos")}</p>
      </div>

      {comparativo && pfy.desde && (
        <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
          <span style={{
            fontSize: "0.76rem", padding: "3px 10px", borderRadius: 5,
            background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)",
            color: "#4ade80", fontWeight: 600,
          }}>
            <i className="fa-solid fa-circle" style={{ fontSize: "0.5rem", marginRight: 5, verticalAlign: "middle" }}/>
            {t("reportes.anioActual")}: {fechaDesde} → {fechaHasta}
          </span>
          <span style={{
            fontSize: "0.76rem", padding: "3px 10px", borderRadius: 5,
            background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.2)",
            color: "var(--text-secondary)", fontWeight: 600,
          }}>
            <i className="fa-solid fa-circle" style={{ fontSize: "0.5rem", marginRight: 5, verticalAlign: "middle" }}/>
            {t("reportes.anioAnterior")}: {pfy.desde} → {pfy.hasta}
            {loadingAnt && <i className="fa-solid fa-circle-notch fa-spin" style={{ marginLeft: 8, fontSize: "0.7rem" }}/>}
          </span>
        </div>
      )}

      <FiltrosBarra
        fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        comparativo={comparativo} setComparativo={setComparativo}
        extraButtons={
          <>
            <CheckPill checked={pdfMensual} onChange={setPdfMensual}
              color="#60a5fa" icon="fa-table-columns" label={t("reportes.pdfMensual")}/>
            <BotonExcel
              onClick={() => excelER.descargar(`fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              loading={excelER.loading}
              disabled={excelER.loading || loading || !data}
            />
            <BotonPDF
              onClick={() => pdfMensual
                ? pdfER.generar(`vista_mensual=true&anio=${(fechaDesde || "").split("-")[0]}`)
                : pdfER.generar(`fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              loading={pdfER.loading}
              disabled={pdfER.loading || loading || !data}
            />
            {pdfER.modal}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label: t("reportes.totalIngresos"), value: tI, color: "#4ade80", icon: "fa-arrow-trend-up" },
          { label: t("reportes.totalGastos"),   value: tG, color: "#f87171", icon: "fa-arrow-trend-down" },
          { label: sup ? t("reportes.superavit") : t("reportes.deficit"),
            value: Math.abs(res), color: sup ? "#f97316" : "#a78bfa",
            icon:  sup ? "fa-circle-check" : "fa-circle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={ingresos.length + gastos.length} loading={loading || loadingAnt}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t("reportes.codigo")}</th>
                <th>{t("reportes.nombre")}</th>
                <th className="text-end" style={{ width: 160 }}>{t("reportes.monto")}</th>
                {comparativo && (
                  <th className="text-end" style={{ width: 160, borderLeft: "1px solid var(--border-card)" }}>
                    {t("reportes.anioAnterior")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={numCols} msg={t("reportes.calculandoResultados")}/>
                : (ingresos.length + gastos.length === 0)
                  ? <FilaVacia colSpan={numCols} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : <>
                    {ingresos.length > 0 && <>
                      <Separador label={t("reportes.ingresos")} color="#4ade80" numCols={numCols}/>
                      {ingresos.map(f => (
                        <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                          onToggle={id => setCollapsed(prev => toggle(prev, id))}
                          colorFn={colorFila} valorFn={valorFila}
                          extraCells={comparativo ? (
                            <td className="text-end" style={{
                              padding: "6px 12px", fontFamily: "monospace", fontSize: "0.82rem",
                              color: valorFilaAnt(f, dataAnt).color,
                              borderLeft: "1px solid var(--border-card)"
                            }}>
                              {valorFilaAnt(f, dataAnt).text}
                            </td>
                          ) : null}
                        />
                      ))}
                      <FilaTotal label={t("reportes.totalIngresos")} monto={tI} montoAnt={tIAnt}
                        color="#4ade80" comparativo={comparativo} fmtCRC={fmtCRC}/>
                    </>}
                    {gastos.length > 0 && <>
                      <Separador label={t("reportes.gastos")} color="#f87171" numCols={numCols}/>
                      {gastos.map(f => (
                        <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                          onToggle={id => setCollapsed(prev => toggle(prev, id))}
                          colorFn={colorFila} valorFn={valorFila}
                          extraCells={comparativo ? (
                            <td className="text-end" style={{
                              padding: "6px 12px", fontFamily: "monospace", fontSize: "0.82rem",
                              color: valorFilaAnt(f, dataAnt).color,
                              borderLeft: "1px solid var(--border-card)"
                            }}>
                              {valorFilaAnt(f, dataAnt).text}
                            </td>
                          ) : null}
                        />
                      ))}
                      <FilaTotal label={t("reportes.totalGastos")} monto={tG} montoAnt={tGAnt}
                        color="#f87171" comparativo={comparativo} fmtCRC={fmtCRC}/>
                    </>}
                    {data && (
                      <tr style={{ background: sup ? "rgba(249,115,22,0.08)" : "rgba(167,139,250,0.08)",
                        borderTop: `3px solid ${sup ? "#f97316" : "#a78bfa"}` }}>
                        <td colSpan={2} style={{ padding: "12px 12px", fontWeight: 800, fontSize: "0.92rem",
                          color: sup ? "#f97316" : "#a78bfa" }}>
                          {sup ? t("reportes.superavitLabel") : t("reportes.deficitLabel")}
                        </td>
                        <td className="text-end" style={{ padding: "12px 14px",
                          fontFamily: "monospace", fontWeight: 800, fontSize: "1rem",
                          color: sup ? "#f97316" : "#a78bfa" }}>
                          {fmtCRC(Math.abs(res))}
                        </td>
                        {comparativo && (
                          <td className="text-end" style={{ padding: "12px 14px",
                            fontFamily: "monospace", fontWeight: 800, fontSize: "1rem",
                            color: "var(--text-secondary)",
                            borderLeft: "1px solid var(--border-card)" }}>
                            {resAnt != null ? fmtCRC(Math.abs(resAnt)) : "—"}
                          </td>
                        )}
                      </tr>
                    )}
                  </>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
